export const MAX_PHOTO_BYTES = 12 * 1024 * 1024;
export const ACCEPTED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const PHOTO_ACCEPT_ATTRIBUTE = ACCEPTED_PHOTO_TYPES.join(',');

export interface PreparedPhoto {
  file: File;
  previewUrl: string;
  analysisCanvas: HTMLCanvasElement;
  width: number;
  height: number;
}

export function validatePhotoFile(file: File): string | null {
  if (!ACCEPTED_PHOTO_TYPES.includes(file.type as (typeof ACCEPTED_PHOTO_TYPES)[number])) {
    return 'Choose a JPG, PNG, or WebP image.';
  }
  if (file.size === 0) {
    return 'This image file is empty. Choose a different photo.';
  }
  if (file.size > MAX_PHOTO_BYTES) {
    return 'This photo is larger than 12 MB. Use a smaller image or crop it first.';
  }
  return null;
}

async function decodePhoto(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch (error) {
    throw new Error('The browser could not decode this image. Try exporting it as a standard JPG or PNG.', {
      cause: error,
    });
  }
}

export async function preparePhoto(file: File, maxDimension = 1600): Promise<PreparedPhoto> {
  const validationError = validatePhotoFile(file);
  if (validationError) throw new Error(validationError);

  const bitmap = await decodePhoto(file);
  const longestSide = Math.max(bitmap.width, bitmap.height);
  const scale = longestSide > maxDimension ? maxDimension / longestSide : 1;
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const analysisCanvas = document.createElement('canvas');
  analysisCanvas.width = width;
  analysisCanvas.height = height;
  const context = analysisCanvas.getContext('2d', { alpha: false });

  if (!context) {
    bitmap.close();
    throw new Error('Canvas image processing is unavailable in this browser.');
  }

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  return {
    file,
    previewUrl: URL.createObjectURL(file),
    analysisCanvas,
    width,
    height,
  };
}

export function releasePreparedPhoto(photo: PreparedPhoto | null): void {
  if (!photo) return;
  URL.revokeObjectURL(photo.previewUrl);
  photo.analysisCanvas.width = 1;
  photo.analysisCanvas.height = 1;
}

export async function canvasToPhotoFile(
  canvas: HTMLCanvasElement,
  filename = 'camera-portrait.jpg',
): Promise<File> {
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) resolve(result);
        else reject(new Error('The camera frame could not be captured.'));
      },
      'image/jpeg',
      0.92,
    );
  });

  return new File([blob], filename, { type: 'image/jpeg', lastModified: Date.now() });
}

export function captureVideoFrame(video: HTMLVideoElement): HTMLCanvasElement {
  if (video.videoWidth === 0 || video.videoHeight === 0) {
    throw new Error('The camera is not ready yet. Hold still for a moment, then capture again.');
  }

  const maxDimension = 1600;
  const longestSide = Math.max(video.videoWidth, video.videoHeight);
  const scale = longestSide > maxDimension ? maxDimension / longestSide : 1;
  const width = Math.round(video.videoWidth * scale);
  const height = Math.round(video.videoHeight * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new Error('Canvas image processing is unavailable in this browser.');

  context.translate(width, 0);
  context.scale(-1, 1);
  context.drawImage(video, 0, 0, width, height);
  return canvas;
}

export function stopMediaStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop());
}

export function describeUnknownError(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}
